import { useAppSelector } from "integrations/redux/hooks"
import { get } from "nodash"
import { useCallback, useRef, useState } from "react"
import { selectAuthenticationLoading } from "user/authentication/selectors"
import CompactAuthenticationManagementForm from "./CompactAuthenticationManagementForm"
import { FederatedGroup } from "./federated/FederatedGroup"
import PasswordInput from "./PasswordInput"
import { FormActionHandlerFunction } from "./types"
import { ButtonAuthenticationPrimary, ButtonAuthenticationTertiary } from "@nova/component-btn"
import { AuthenticationInput } from "./inputs/AuthenticationInput"
import * as Icons from "@nova/component-icons"
import { AuthenticationErrorMessage } from "./AuthenticationErrorMessage"

type LoginFormProps = {
    email?: string
    message?: string
    primaryClickHandler: (values: { email: string; password: string }) => void
    secondaryClickHandler: FormActionHandlerFunction
    setIsVerifyingSso: (isVerifyingSso: boolean) => void
}

export default function LoginForm({
    email,
    message,
    primaryClickHandler,
    secondaryClickHandler,
    setIsVerifyingSso,
}: LoginFormProps) {
    const isLoading = useAppSelector(selectAuthenticationLoading)

    const [state, setState] = useState<{
        hasErrors: boolean
    }>({
        hasErrors: false,
    })

    const emailRef = useRef<HTMLInputElement | null>(null)
    const passwordRef = useRef<HTMLInputElement | null>(null)

    // observer, because it doesn't actually modify the value, i
    const validateChange = useCallback((e: { target: { value: string; name: string } }) => {
        const value = get(e, "target.value")
        const name = get(e, "target.name")

        if (name === "email" || name === "password") {
            // some very basic error handling for now. expand as needed
            setState((state) => ({ ...state, hasErrors: Boolean(!value) }))
        }
    }, [])

    const submitHandler = useCallback(
        (values: { email?: string; password?: string }) => {
            const { email, password } = values
            if (!email || !password) {
                // the individual forms should handle catching this problem!! if we get to here, things need to be dialed.
                throw "Missing email or password."
            }
            const trimmedPassword = password.trim()
            primaryClickHandler({ email, password: trimmedPassword })
        },
        [primaryClickHandler]
    )

    const { hasErrors } = state

    return (
        <div className="flex flex-col gap-4">
            <FederatedGroup setIsVerifyingSso={setIsVerifyingSso} />
            <CompactAuthenticationManagementForm submitHandler={submitHandler}>
                <AuthenticationInput
                    inputRef={emailRef}
                    id="email"
                    name="email"
                    label="Email*"
                    type="email"
                    onChange={validateChange}
                    defaultValue={email}
                    disabled={isLoading}
                    leftIcon={Icons.Email}
                />

                <PasswordInput
                    inputRef={passwordRef}
                    id="password"
                    name="password"
                    changeHandler={validateChange}
                    isDisabled={isLoading}
                />
                {message && <AuthenticationErrorMessage message={message} />}
                <ButtonAuthenticationPrimary
                    className="authentication-management-button"
                    data-tid="authentication-submit"
                    disabled={
                        hasErrors ||
                        isLoading ||
                        !emailRef.current?.value ||
                        !passwordRef.current?.value
                    }
                    title="Log in"
                    isLoading={isLoading}
                />
                <ButtonAuthenticationTertiary
                    onClick={() => secondaryClickHandler()}
                    title="Forgot Password"
                    color="#5E5A5B"
                    disabled={isLoading}
                />
            </CompactAuthenticationManagementForm>
        </div>
    )
}
